import React, { useEffect, useState, useCallback } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import DataTable from "../../components/DataTable/DataTable";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Autocomplete,
  Card,
  CardContent,
} from "@mui/material";
import locSchema from "./schemas/location.json";
import { Routes, Route } from "react-router-dom";

const fieldSx = { "& .MuiInputBase-input": { py: 0.25, fontSize: 12 } };
const autoSx = {
  "& .MuiOutlinedInput-root": { padding: "4px 32px 4px 14px !important", fontSize: 12 },
  "& .MuiOutlinedInput-root .MuiAutocomplete-input": { padding: "0 !important", fontSize: 12 },
  "& .MuiAutocomplete-popupIndicator": { padding: "1px", marginRight: "-2px" },
  "& .MuiAutocomplete-clearIndicator": { padding: "1px" },
};

function FormRow({ label, children, alignStart }) {
  return (
    <div
      className={`grid grid-cols-[140px_1fr] gap-2 py-px ${alignStart ? "items-start" : "items-center"}`}
    >
      <span className={`text-xs text-gray-600 whitespace-nowrap ${alignStart ? "pt-2" : ""}`}>
        {label}
      </span>
      {children}
    </div>
  );
}

export default function Locations() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const SESSION_PAGE_SIZE_KEY = "compliance_ui_page_size";
  const defaultSize = (() => {
    try {
      const s = sessionStorage.getItem(SESSION_PAGE_SIZE_KEY);
      const n = Number(s);
      if (n) return n;
    } catch (e) {}
    return 20;
  })();
  const [size, setSize] = useState(defaultSize);
  const [total, setTotal] = useState(0);
  const [activeFilters, setActiveFilters] = useState({});

  const handleFiltersChange = React.useCallback((filters) => {
    setActiveFilters((prev) => {
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(filters || {});
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every((k) => String(prev[k] || "") === String(filters[k] || ""))
      ) {
        return prev;
      }
      return filters;
    });
  }, []);

  async function load(p = page, s = size, filters = activeFilters) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", p);
      params.set("size", s);
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value) params.set(`filter.${key}`, value);
      });
      const data = await apiGet(`/api/locations?${params.toString()}`);
      const content = Array.isArray(data) ? data : data.content || [];
      setList(content);
      setTotal(data.totalElements || content.length || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const buildFilterQuery = React.useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(activeFilters).forEach(([key, value]) => {
      if (value) params.set(`filter.${key}`, value);
    });
    return params.toString();
  }, [activeFilters]);

  useEffect(() => {
    load(page, size, activeFilters);
  }, [page, size, buildFilterQuery]);

  const handlePageChange = React.useCallback((newPage) => { setPage(newPage); }, [load, size, activeFilters]);

  const handlePageSizeChange = React.useCallback(
    (newSize) => {
      try { sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, String(newSize)); } catch (e) {}
      setSize(newSize);
      setPage(0);
    },
    [load, activeFilters],
  );

  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [selectedParent, setSelectedParent] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  function openAddDialog() {
    setNewName("");
    setNewCode("");
    setSelectedParent(null);
    setEditingItem(null);
    setAddOpen(true);
  }

  function openEditDialog(item) {
    setEditingItem(item);
    setNewName(item.name || "");
    setNewCode(item.code || "");
    setSelectedParent(item.parentId ? list.find((x) => x.id === item.parentId) : null);
    setAddOpen(true);
  }

  async function handleDelete(item) {
    if (!window.confirm("Delete location " + item.name + "?")) return;
    try {
      await apiDelete(`/api/locations/${item.id}`);
      load();
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  }

  async function handleSave() {
    if (!newName || newName.trim() === "") {
      alert("Name is required");
      return;
    }
    try {
      if (editingItem) {
        await apiPut(`/api/locations/${editingItem.id}`, {
          ...editingItem,
          name: newName.trim(),
          code: newCode || null,
          parentId: selectedParent ? selectedParent.id : null,
        });
      } else {
        await apiPost("/api/locations", {
          name: newName.trim(),
          code: newCode || null,
          parentId: selectedParent ? selectedParent.id : null,
          enabled: true,
        });
      }
      setAddOpen(false);
      setEditingItem(null);
      load();
    } catch (err) {
      alert("Save failed: " + err.message);
    }
  }

  const getFullName = useCallback(
    (loc) => {
      if (!loc) return "";
      const parts = [loc.name];
      let parentId = loc.parentId;
      while (parentId) {
        const p = list.find((x) => x.id === parentId);
        if (!p) break;
        parts.push(p.name);
        parentId = p.parentId;
      }
      return parts.join(" — ");
    },
    [list],
  );

  return (
    <div className="h-[89.8vh]">
      <Routes>
        <Route
          index
          element={
            <Box sx={{ m: 1 }}>
              <Card>
                <CardContent>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6">Locations</Typography>
                    <Button variant="contained" color="primary" onClick={openAddDialog}>
                      Add Location
                    </Button>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <DataTable
                      schema={locSchema}
                      rows={list}
                      onFiltersChange={handleFiltersChange}
                      onPageSizeChange={handlePageSizeChange}
                      totalCount={total}
                      currentPage={page}
                      onPageChange={handlePageChange}
                      extraControls={null}
                      onEdit={(_, row) => openEditDialog(row)}
                      onDelete={(_, row) => handleDelete(row)}
                    />
                    {loading ? <div style={{ padding: 12 }}>Loading...</div> : null}
                  </div>
                </CardContent>
              </Card>
            </Box>
          }
        />
      </Routes>

      <Dialog
        open={addOpen}
        onClose={() => { setAddOpen(false); setEditingItem(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1 }}>
          {editingItem ? "Edit Location" : "Add Location"}
        </DialogTitle>
        <DialogContent dividers>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "4px 0" }}>
            <FormRow label="Name">
              <TextField
                size="small"
                margin="dense"
                fullWidth
                sx={fieldSx}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
              />
            </FormRow>
            <FormRow label="Code">
              <TextField
                size="small"
                margin="dense"
                fullWidth
                sx={fieldSx}
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
              />
            </FormRow>
            <FormRow label="Parent Location">
              <Autocomplete
                sx={autoSx}
                options={list}
                getOptionLabel={(opt) => (opt ? getFullName(opt) : "")}
                value={selectedParent}
                onChange={(_, v) => setSelectedParent(v)}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                fullWidth
                clearOnEscape
                renderInput={(params) => (
                  <TextField {...params} size="small" margin="dense" />
                )}
                ListboxProps={{ style: { maxHeight: 240 } }}
              />
            </FormRow>
          </div>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => { setAddOpen(false); setEditingItem(null); }}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave}>
            {editingItem ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
